import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PromptTips } from './PromptTips';
import { 
  INDUSTRIES, 
  TONES, 
  COMPLEXITY_LEVELS, 
  OUTPUT_FORMATS,
  PromptData 
} from '../types';

export function PromptForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<PromptData>({
    topic: '',
    goal: '',
    industry: INDUSTRIES[0],
    audience: '',
    tone: TONES[0],
    complexity: COMPLEXITY_LEVELS[0],
    format: OUTPUT_FORMATS[0]
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.topic || !formData.goal || !formData.industry || !formData.audience) {
      alert('Please fill in all required fields');
      return;
    }
    navigate('/result', { state: { promptData: formData } });
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <h2 className="text-2xl font-bold text-center mb-8">Create Your AI Prompt</h2>
      
      <PromptTips />

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <label className="block">
            <span className="text-gray-700 font-medium">What topic do you need help with?*</span>
            <input
              type="text"
              value={formData.topic}
              onChange={(e) => setFormData(prev => ({ ...prev, topic: e.target.value }))}
              placeholder="E.g., JavaScript programming, digital marketing, creative writing"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </label>

          <label className="block">
            <span className="text-gray-700 font-medium">What is your specific goal?*</span>
            <input
              type="text"
              value={formData.goal}
              onChange={(e) => setFormData(prev => ({ ...prev, goal: e.target.value }))}
              placeholder="E.g., Write a blog post outline, Create a marketing strategy"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </label>

          <label className="block">
            <span className="text-gray-700 font-medium">Who is your target audience?*</span>
            <input
              type="text"
              value={formData.audience}
              onChange={(e) => setFormData(prev => ({ ...prev, audience: e.target.value }))}
              placeholder="E.g., Marketing professionals, Students, etc."
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </label>

          <label className="block">
            <span className="text-gray-700 font-medium">Select your industry*</span>
            <select
              value={formData.industry}
              onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              {INDUSTRIES.map(industry => (
                <option key={industry} value={industry}>{industry}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-gray-700 font-medium">Preferred tone of voice</span>
            <select
              value={formData.tone}
              onChange={(e) => setFormData(prev => ({ ...prev, tone: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              {TONES.map(tone => (
                <option key={tone} value={tone}>{tone}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-gray-700 font-medium">Content complexity level</span>
            <select
              value={formData.complexity}
              onChange={(e) => setFormData(prev => ({ ...prev, complexity: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              {COMPLEXITY_LEVELS.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-gray-700 font-medium">Preferred output format</span>
            <select
              value={formData.format}
              onChange={(e) => setFormData(prev => ({ ...prev, format: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              {OUTPUT_FORMATS.map(format => (
                <option key={format} value={format}>{format}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="pt-6 text-center">
          <button
            type="submit"
            className="bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Generate Prompt
          </button>
          <p className="text-sm text-gray-500 mt-4">* Required fields</p>
        </div>
      </form>
    </div>
  );
}